import { ReactNode, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';

import { PushLockNote } from './PushLockNote';
import { PushTextField } from './PushTextField';
import {
  addPushOverride,
  PushBroadcastData,
  PushBroadcastDraft,
  PushBroadcastOptions,
  pushDestinationChoices,
  pushDestinationFromKey,
  pushDestinationKey,
  pushLanguageName,
  pushOverrideLanguages,
  removePushOverride,
  setPushText,
  sourceLanguageOf,
} from '../utils';

type Props = {
  draft: PushBroadcastDraft;
  onChange: (draft: PushBroadcastDraft) => void;
  options: PushBroadcastOptions;
  // The saved destination, kept selectable when it is not offered any more.
  savedDestination: PushBroadcastData['destination'] | null;
  disabled: boolean;
  // Why every field is off (not a draft), repeated in each card so it is read with the fields; null when editable.
  lockedReason: string | null;
  // The translations spreadsheet, shown above the English texts so the overrides list never pushes it off screen.
  translationsSpreadsheet?: ReactNode;
};

/**
 * What the player receives: name (admin only), opt-out category, destination, the English texts (mandatory) and the
 * per-language overrides. There is no "English only" mode: it is simply the state with no override.
 */
export function PushBroadcastEditor({
  draft,
  onChange,
  options,
  savedDestination,
  disabled,
  lockedReason,
  translationsSpreadsheet = null,
}: Props): JSX.Element {
  const {
    pushBroadcastLanguages: languages,
    pushBroadcastLimits: limits,
    pushBroadcastSettingsCategories: categories,
    pushBroadcastDestinations: destinations,
  } = options;
  const sourceLanguage = sourceLanguageOf(languages);
  const overrides = pushOverrideLanguages(draft, sourceLanguage, languages);
  const addable = languages.filter(({ code }) => code !== sourceLanguage && !overrides.includes(code));
  const [languageToAdd, setLanguageToAdd] = useState('');

  const destinationChoices = pushDestinationChoices(destinations, savedDestination);
  const destinationKey = pushDestinationKey(draft.destination);
  const selectedDestination = destinationChoices.find((choice) => pushDestinationKey(choice) === destinationKey);

  const textFields = (language: string, idPrefix: string) => (
    <>
      <PushTextField
        id={`${idPrefix}-title-${language}`}
        label="Title"
        value={draft.titleTexts[language] ?? ''}
        onChange={(text) => onChange(setPushText(draft, 'titleTexts', language, text))}
        maxLength={limits.titleMaxLength}
        warnLength={limits.titleWarnLength}
        disabled={disabled}
      />
      <PushTextField
        id={`${idPrefix}-body-${language}`}
        label="Body"
        value={draft.bodyTexts[language] ?? ''}
        onChange={(text) => onChange(setPushText(draft, 'bodyTexts', language, text))}
        maxLength={limits.bodyMaxLength}
        warnLength={limits.bodyWarnLength}
        multiline
        disabled={disabled}
      />
    </>
  );

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="font-weight-bold">Push</Card.Header>
        <Card.Body>
          <PushLockNote reason={lockedReason} />
          <PushTextField
            id="push-broadcast-name"
            label="Name (admin only, players never see it)"
            value={draft.name}
            onChange={(name) => onChange({ ...draft, name })}
            maxLength={limits.nameMaxLength}
            disabled={disabled}
          />

          <Form.Group className="mb-3">
            <Form.Label className="mb-1">
              Opt-out category <span className="text-danger">*</span>
            </Form.Label>
            <Form.Text muted className="mt-0 mb-2">
              Players who turned this category off in the game settings never receive the push. Required, no default.
            </Form.Text>
            {categories.map(({ category, label, description }) => (
              <Form.Check
                key={category}
                type="radio"
                id={`push-broadcast-category-${category}`}
                name="push-broadcast-category"
                checked={draft.settingsCategory === category}
                disabled={disabled}
                onChange={() => onChange({ ...draft, settingsCategory: category })}
                label={
                  <span>
                    <span className="font-weight-bold">{label}</span>
                    <span className="text-muted"> · {description}</span>
                  </span>
                }
              />
            ))}
            {draft.settingsCategory && !categories.some(({ category }) => category === draft.settingsCategory) ? (
              <Alert variant="warning" className="mt-2 mb-0 py-2">
                The saved category &quot;{draft.settingsCategory}&quot; is not offered any more: pick one above.
              </Alert>
            ) : null}
          </Form.Group>

          <Form.Group controlId="push-broadcast-destination" className="mb-0">
            <Form.Label className="mb-1">Destination (where a tap opens the game)</Form.Label>
            <Form.Control
              as="select"
              value={destinationKey}
              disabled={disabled}
              onChange={(event) => onChange({ ...draft, destination: pushDestinationFromKey(event.target.value) })}
            >
              {destinationChoices.map((choice) => (
                <option key={pushDestinationKey(choice)} value={pushDestinationKey(choice)}>
                  {choice.label}
                </option>
              ))}
            </Form.Control>
            {selectedDestination?.caveat ? (
              <Form.Text className="text-warning">{selectedDestination.caveat}</Form.Text>
            ) : null}
          </Form.Group>
        </Card.Body>
      </Card>

      {/* The text of the notification, one section: the spreadsheet first, so a long overrides list never pushes it
          out of view, then the English texts, then the per-language overrides. */}
      <Card className="mb-3">
        <Card.Header className="font-weight-bold">Notification text</Card.Header>
        <Card.Body>
          {translationsSpreadsheet ? <div className="pb-3 mb-3 border-bottom">{translationsSpreadsheet}</div> : null}
          <section className="pb-3 mb-3 border-bottom">
            <h6 className="font-weight-bold mb-2">
              {pushLanguageName(sourceLanguage, languages)} <span className="text-danger">*</span>
              <span className="font-weight-normal text-muted">
                {' '}
                · mandatory, and what every player without an override receives
              </span>
            </h6>
            <PushLockNote reason={lockedReason} />
            {textFields(sourceLanguage, 'push-source')}
            {limits.placeholders.length > 0 ? (
              <Form.Text muted>
                Placeholders:{' '}
                {limits.placeholders.map((placeholder) => (
                  <code key={placeholder}>{placeholder} </code>
                ))}
                (replaced per player, spelled exactly so in every language).
              </Form.Text>
            ) : null}
          </section>
          <section>
            <h6 className="font-weight-bold mb-2">
              Language overrides ({overrides.length})
              <span className="font-weight-normal text-muted">
                {' '}
                · optional, a language without one receives English
              </span>
            </h6>
            <PushLockNote reason={lockedReason} />
            {overrides.length === 0 ? (
              <div className="text-muted mb-3">No override: every player receives English.</div>
            ) : (
              <Table size="sm" className="mb-3">
                <tbody>
                  {overrides.map((language) => (
                    <tr key={language}>
                      <td style={{ width: '9rem' }} className="align-top pt-3">
                        <div className="font-weight-bold">{pushLanguageName(language, languages)}</div>
                        <code>{language}</code>
                        <div className="mt-2">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={disabled}
                            onClick={() => onChange(removePushOverride(draft, language))}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                      <td>{textFields(language, 'push-override')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Row className="align-items-center">
              <Col xs="auto">
                <Form.Control
                  as="select"
                  size="sm"
                  value={languageToAdd}
                  disabled={disabled || addable.length === 0}
                  onChange={(event) => setLanguageToAdd(event.target.value)}
                  aria-label="Language to add"
                >
                  <option value="">
                    {addable.length === 0 ? 'Every language has an override' : 'Add a language…'}
                  </option>
                  {addable.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={disabled || !languageToAdd}
                  onClick={() => {
                    onChange(addPushOverride(draft, languageToAdd));
                    setLanguageToAdd('');
                  }}
                >
                  Add override
                </Button>
              </Col>
            </Row>
          </section>
        </Card.Body>
      </Card>
    </>
  );
}
